"use client";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Flex } from "@radix-ui/themes";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { ComponentProps } from "react";
import { useCommSettingsStore } from "@/app/voice-settings";

interface IDTPicker extends ComponentProps<typeof Flex> {
    title: string;
    date: string;
    onDateChange: (date: string) => void;
}

// date should be UTC/GMT date time string
export default function DTPicker({ title, date, onDateChange, ...flexProps }: IDTPicker) {
    const { commSettings } = useCommSettingsStore();

    const handleChange = (newDate: Date | null) => {
        // While the user is typing, MUI reports every partially entered value as an Invalid Date.
        // Propagating it would push an empty string back into the controlled value on each keystroke,
        // which resets the field and makes it impossible to type. Keep those intermediate states
        // inside the picker and only publish a cleared ('') or a complete, valid date.
        if (newDate && newDate.toString() === "Invalid Date") return;
        onDateChange(newDate ? fromZonedTime(newDate, commSettings.timeZone?.userTimeZone as string).toISOString() : "");
    };

    return (
        <Flex {...flexProps}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                {/* DesktopDateTimePicker, not the responsive DateTimePicker: the responsive one switches
                    to the mobile variant on coarse-pointer devices, where the field is read-only by
                    design and can only be set through the dialog. This screen needs it to be typeable. */}
                <DesktopDateTimePicker
                    label={title}
                    // this will convert the GMT timezone date string to timezone specific string & displays it.
                    // An empty date must map to null (not an Invalid Date) - MUI treats two Invalid Dates as
                    // different values and would reset the input on every render.
                    value={date ? toZonedTime(date, commSettings.timeZone?.userTimeZone as string) : null}
                    onChange={handleChange}
                />
            </LocalizationProvider>
        </Flex>
    );
}
